import { useCallback, useEffect, useRef, useState } from 'react';

export interface Coords {
  lat: number;
  lng: number;
  accuracy?: number;
  heading?: number;
}

const R = 6371000;
const rad = (d: number) => (d * Math.PI) / 180;

export function haversineM(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function bearingDeg(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const dLng = rad(bLng - aLng);
  const y = Math.sin(dLng) * Math.cos(rad(bLat));
  const x =
    Math.cos(rad(aLat)) * Math.sin(rad(bLat)) -
    Math.sin(rad(aLat)) * Math.cos(rad(bLat)) * Math.cos(dLng);
  return (Math.atan2(y, x) * (180 / Math.PI) + 360) % 360;
}

const COMPASS_POINTS = [
  'ahead',
  'ahead-right',
  'right',
  'behind-right',
  'behind',
  'behind-left',
  'left',
  'ahead-left',
] as const;

/**
 * Turns a person-relative bearing (0° = straight ahead of you, clockwise) into
 * a plain-language 8-point direction, e.g. 42° → "ahead-right". Used next to
 * the rotated arrow glyph so direction reads instantly, not just visually.
 */
export function compassLabel(deg: number | null | undefined): string | null {
  if (deg == null || !Number.isFinite(deg)) return null;
  const norm = ((deg % 360) + 360) % 360;
  return COMPASS_POINTS[Math.round(norm / 45) % 8] ?? null;
}

interface RawFix {
  lat: number;
  lng: number;
  accuracy: number;
}

/**
 * Blend a new GPS fix into the running one so the "you" pip and every
 * distance/bearing don't jitter on each `watchPosition` tick. The more accurate
 * fix gets the bigger weight; a jump larger than ~30 m is treated as real
 * movement and snaps through untouched.
 */
export function blendFix(prev: RawFix | null, next: RawFix): RawFix {
  if (!prev) return next;
  if (haversineM(prev.lat, prev.lng, next.lat, next.lng) > 30) return next;
  const pa = prev.accuracy > 0 ? prev.accuracy : 50;
  const na = next.accuracy > 0 ? next.accuracy : 50;
  const w = Math.min(0.9, Math.max(0.15, pa / (pa + na)));
  return {
    lat: prev.lat + (next.lat - prev.lat) * w,
    lng: prev.lng + (next.lng - prev.lng) * w,
    accuracy: prev.accuracy + (na - prev.accuracy) * w,
  };
}

export type GeoPermission = 'unknown' | 'prompt' | 'granted' | 'denied';

interface GeoState {
  coords: Coords | null;
  permission: GeoPermission;
  error: string | null;
  loading: boolean;
}

/**
 * Wraps `watchPosition` + the Permissions API + compass heading. Only starts
 * watching when `enabled` is true (so we never ask for location on pages that
 * don't need it).
 */
export function useGeolocation(
  enabled: boolean,
): GeoState & { requestOnce: () => Promise<Coords | null> } {
  const [state, setState] = useState<GeoState>({
    coords: null,
    permission: 'unknown',
    error: null,
    loading: false,
  });
  const headingRef = useRef<number | undefined>(undefined);
  const fixRef = useRef<RawFix | null>(null);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('permissions' in navigator)) return;
    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((res) => {
        setState((s) => ({ ...s, permission: res.state as GeoPermission }));
        res.onchange = () => setState((s) => ({ ...s, permission: res.state as GeoPermission }));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onOrient = (e: DeviceOrientationEvent) => {
      // webkitCompassHeading (iOS) or 360 - alpha (spec).
      const wk = (e as unknown as { webkitCompassHeading?: number }).webkitCompassHeading;
      if (typeof wk === 'number') headingRef.current = wk;
      else if (e.absolute && typeof e.alpha === 'number')
        headingRef.current = (360 - e.alpha) % 360;
    };
    window.addEventListener('deviceorientationabsolute', onOrient as EventListener, true);
    window.addEventListener('deviceorientation', onOrient as EventListener, true);
    return () => {
      window.removeEventListener('deviceorientationabsolute', onOrient as EventListener, true);
      window.removeEventListener('deviceorientation', onOrient as EventListener, true);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !navigator.geolocation) return;
    setState((s) => ({ ...s, loading: true }));
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const blended = blendFix(fixRef.current, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy || 50,
        });
        fixRef.current = blended;
        // GPS course-over-ground is a solid heading fallback while actually
        // moving; the phone compass wins when it's reporting.
        const moving = (pos.coords.speed ?? 0) > 0.7;
        const gpsHeading =
          moving && pos.coords.heading != null && Number.isFinite(pos.coords.heading)
            ? pos.coords.heading
            : undefined;
        setState({
          coords: {
            lat: blended.lat,
            lng: blended.lng,
            accuracy: blended.accuracy,
            heading: headingRef.current ?? gpsHeading,
          },
          permission: 'granted',
          error: null,
          loading: false,
        });
      },
      (err) => {
        setState((s) => ({
          ...s,
          loading: false,
          permission: err.code === err.PERMISSION_DENIED ? 'denied' : s.permission,
          error: err.message,
        }));
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [enabled]);

  /**
   * One-shot `getCurrentPosition`. Resolves with the fix (or `null` if it was
   * denied/unavailable) so a caller triggered by a tap can `await` it and act
   * on the result in the same gesture — the browser only reliably shows the
   * permission prompt when the call is inside a user gesture, so this is the
   * moment that matters, not the passive `watchPosition` on mount.
   */
  const requestOnce = useCallback((): Promise<Coords | null> => {
    return new Promise((resolve) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null);
      setState((s) => ({ ...s, loading: true }));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords: Coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            heading: headingRef.current,
          };
          setState({ coords, permission: 'granted', error: null, loading: false });
          resolve(coords);
        },
        (err) => {
          setState((s) => ({
            ...s,
            loading: false,
            permission: err.code === err.PERMISSION_DENIED ? 'denied' : s.permission,
            error: err.message,
          }));
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 15000 },
      );
    });
  }, []);

  return { ...state, requestOnce };
}

/** iOS 13+ requires a user gesture to unlock compass events. */
export async function requestCompassPermission(): Promise<void> {
  const anyOrient = DeviceOrientationEvent as unknown as {
    requestPermission?: () => Promise<string>;
  };
  if (typeof anyOrient.requestPermission === 'function') {
    try {
      await anyOrient.requestPermission();
    } catch {
      /* user declined — heading just stays undefined */
    }
  }
}
